import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Plus, Trash2, ArrowUp, ArrowDown, AlertTriangle, Info, CalendarClock } from 'lucide-react';
import ModalShell, { ModalFooter } from '../common/ModalShell';
import { createProgramme, updateProgramme } from '../../redux/programmeSlice';
import {
    LIMITS, digitsOnly, emptyMilestone, emptyProgrammeDraft, programmeToDraft, draftToBody,
    validateProgrammeDraft, hasProgrammeErrors, switchDraftUnit, spanToDays,
} from '../../utils/careProgrammes';
import { serverReason } from '../patientTabs/prescriptionHelpers';

// THE PROGRAMME BUILDER — one editor for a doctor's own programmes and the
// hospital's (ownerType 'tenant'). Everything is the user's: the name, what
// the start date is called, the expected length (or open-ended), and every
// STOP — one visit each (doc/programme-visits.md): its name, when it falls
// after the start (days, or weeks + days) and its window (± days, both edges
// count). A stop carries NO check set (owner, 21 Oct 2026: check sets are the
// general tests every appointment shows, never part of a programme), so none
// is offered and none is sent. Nothing is offered pre-filled (owner, 26 Sep
// 2026: nothing speciality-specific). A hospital programme adds a free
// "Meant for".
//
// Weeks are a way of TYPING: the server takes days (weeks × 7 + days). An
// existing milestone keeps its key (patients' hand ticks are stored by it);
// a removed one disappears from the template, and programmes already started
// keep it in their own snapshot. The server re-checks every rule; its refusal
// lands next to the milestone it names (`index`) or the field (`field`).

function SpanInput({ span, unit, onChange, invalid, testId, label }) {
    const { t } = useTranslation();
    const box = `w-20 rounded-xl border px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40 focus:border-[#4ABDBD] ${invalid ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`;
    const total = spanToDays(span, unit);
    return (
        <span className="inline-flex flex-wrap items-center gap-1.5" data-testid={testId}>
            {unit === 'weeks' && (
                <>
                    <input
                        type="text"
                        inputMode="numeric"
                        data-testid={`${testId}-weeks`}
                        aria-label={`${label} — ${t('programmes.weeks_short')}`}
                        value={span.weeks}
                        onChange={(e) => onChange({ ...span, weeks: digitsOnly(e.target.value) })}
                        className={box}
                    />
                    <span className="text-xs text-gray-500">{t('programmes.weeks_short')}</span>
                    <span className="text-xs text-gray-400">+</span>
                </>
            )}
            <input
                type="text"
                inputMode="numeric"
                data-testid={`${testId}-days`}
                aria-label={`${label} — ${t('programmes.days_short')}`}
                value={span.days}
                onChange={(e) => onChange({ ...span, days: digitsOnly(e.target.value) })}
                className={box}
            />
            <span className="text-xs text-gray-500">{t('programmes.days_short')}</span>
            {unit === 'weeks' && Number.isInteger(total) && (
                <span data-testid={`${testId}-total`} className="text-[11px] text-gray-400">{t('programmes.day_equals', { n: total })}</span>
            )}
        </span>
    );
}

function ProgrammeEditor({ isOpen, ownerType = 'doctor', doctorId = null, programme = null, onClose, onSaved }) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const saving = useSelector((state) => state.programmes?.saving);
    const isNew = !programme;
    const [draft, setDraft] = useState(emptyProgrammeDraft);
    const [initialKey, setInitialKey] = useState('');
    const [showErrors, setShowErrors] = useState(false);
    const [serverErrors, setServerErrors] = useState({});
    const [serverFieldErrors, setServerFieldErrors] = useState({});
    const [serverError, setServerError] = useState('');
    const nameRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const next = programme ? programmeToDraft(programme) : emptyProgrammeDraft();
        setDraft(next);
        setInitialKey(JSON.stringify(draftToBody(next, ownerType)));
        setShowErrors(false);
        setServerErrors({});
        setServerFieldErrors({});
        setServerError('');
        setTimeout(() => nameRef.current?.focus(), 0);
    }, [isOpen, programme, ownerType, doctorId, dispatch]);

    const errors = useMemo(() => validateProgrammeDraft(draft, { ownerType }), [draft, ownerType]);
    const dirty = isOpen && JSON.stringify(draftToBody(draft, ownerType)) !== initialKey;

    const requestClose = async () => {
        if (saving) return;
        if (dirty) {
            const res = await Swal.fire({
                title: t('programmes.discard_title'),
                text: t('programmes.discard_text'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('programmes.discard_confirm'),
                cancelButtonText: t('programmes.keep_editing'),
                confirmButtonColor: '#d33',
                cancelButtonColor: '#9ca3af',
            });
            if (!res?.isConfirmed) return;
        }
        onClose?.();
    };

    const requestCloseRef = useRef(requestClose);
    useEffect(() => { requestCloseRef.current = requestClose; });
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') requestCloseRef.current?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen]);

    if (!isOpen || typeof document === 'undefined') return null;

    const patch = (changes, field = null) => {
        setDraft((prev) => ({ ...prev, ...changes }));
        setServerError('');
        if (field) setServerFieldErrors((prev) => ({ ...prev, [field]: '' }));
        if ('duration' in changes || 'openEnded' in changes || 'unit' in changes) setServerErrors({});
    };
    const patchMilestone = (index, changes) => {
        setDraft((prev) => ({ ...prev, milestones: prev.milestones.map((m, i) => (i === index ? { ...m, ...changes } : m)) }));
        setServerErrors((prev) => {
            if (!prev[index]) return prev;
            const next = { ...prev };
            delete next[index];
            return next;
        });
    };
    const addMilestone = () => {
        if (draft.milestones.length >= LIMITS.milestonesMax) return;
        setDraft((prev) => ({ ...prev, milestones: [...prev.milestones, emptyMilestone(prev.unit)] }));
    };
    const removeMilestone = (index) => {
        setDraft((prev) => ({ ...prev, milestones: prev.milestones.filter((_, i) => i !== index) }));
        setServerErrors({});
    };
    const moveMilestone = (index, dir) => {
        const target = index + dir;
        if (target < 0 || target >= draft.milestones.length) return;
        setDraft((prev) => {
            const milestones = [...prev.milestones];
            [milestones[index], milestones[target]] = [milestones[target], milestones[index]];
            return { ...prev, milestones };
        });
        setServerErrors({});
    };

    const save = async () => {
        if (saving) return;
        setShowErrors(true);
        if (hasProgrammeErrors(errors)) return;
        const body = draftToBody(draft, ownerType);
        setServerError('');
        setServerErrors({});
        setServerFieldErrors({});
        let res;
        if (isNew) {
            const createBody = { ownerType, ...body };
            if (ownerType === 'doctor' && doctorId) createBody.doctorId = doctorId;
            res = await dispatch(createProgramme({ body: createBody }));
        } else {
            res = await dispatch(updateProgramme({ programmeId: programme.programmeId, changes: body }));
        }
        const ok = isNew ? createProgramme.fulfilled.match(res) : updateProgramme.fulfilled.match(res);
        if (ok) {
            toast.success(t('programmes.saved'));
            onSaved?.(res.payload?.programme || null);
            onClose?.();
            return;
        }
        const refusal = res.payload || {};
        const message = serverReason(refusal, t('programmes.save_failed'), i18n.language);
        if (typeof refusal.index === 'number' && draft.milestones[refusal.index]) {
            setServerErrors({ [refusal.index]: message });
        } else if (['name', 'startLabel', 'meantFor'].includes(refusal.field)) {
            setServerFieldErrors({ [refusal.field]: message });
        } else {
            setServerError(message);
        }
    };

    const tr = (e) => {
        if (!e) return '';
        return typeof e === 'string' ? t(`programmes.${e}`) : t(`programmes.${e.key}`, e.params);
    };
    const shown = (part) => (showErrors ? tr(errors[part]) : '') || serverFieldErrors[part] || '';
    const milestoneErr = (index, part) => (showErrors ? tr(errors.byIndex?.[index]?.[part]) : '');
    const titleKey = isNew ? (ownerType === 'tenant' ? 'editor_new_hospital_title' : 'editor_new_title') : 'editor_edit_title';
    const input = 'w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40 focus:border-[#4ABDBD]';
    const nameErr = shown('name');
    const labelErr = shown('startLabel');
    const meantErr = shown('meantFor');
    const durationErr = showErrors ? tr(errors.duration) : '';

    return createPortal(
        // Click on the dimmed page asks to close, as before the shared frame.
        <div onClick={(e) => { if (e.target === e.currentTarget.firstElementChild) requestClose(); }}>
            <ModalShell
                title={t(`programmes.${titleKey}`)}
                onClose={requestClose}
                closeLabel={t('programmes.close')}
                size="3xl"
                zClass="z-[60]"
                testId="programme-editor-root"
                closeTestId="programme-editor-close"
                contentTestId="programme-editor"
                footer={(
                    <ModalFooter
                        onCancel={requestClose}
                        cancelLabel={t('programmes.cancel')}
                        onSubmit={save}
                        submitLabel={saving ? t('programmes.saving') : t('programmes.save_programme')}
                        busy={saving}
                        cancelTestId="programme-cancel"
                        submitTestId="programme-save"
                    />
                )}
            >
                <div className="space-y-5">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className={ownerType === 'tenant' ? '' : 'sm:col-span-2'}>
                            <label htmlFor="programme-name" className="mb-1 block text-xs font-semibold text-gray-600">{t('programmes.name_label')}</label>
                            <input
                                id="programme-name"
                                ref={nameRef}
                                data-testid="programme-name"
                                type="text"
                                maxLength={LIMITS.name}
                                value={draft.name}
                                placeholder={t('programmes.name_placeholder')}
                                onChange={(e) => patch({ name: e.target.value.replace(/[<>]/g, '') }, 'name')}
                                aria-invalid={Boolean(nameErr)}
                                className={`${input} ${nameErr ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                            />
                            {nameErr && <p data-testid="programme-name-error" className="mt-1 text-xs text-red-600">{nameErr}</p>}
                        </div>
                        {ownerType === 'tenant' && (
                            <div>
                                <label htmlFor="programme-meant-for" className="mb-1 block text-xs font-semibold text-gray-600">{t('programmes.meant_for_label')}</label>
                                <input
                                    id="programme-meant-for"
                                    data-testid="programme-meant-for"
                                    type="text"
                                    maxLength={LIMITS.meantFor}
                                    value={draft.meantFor}
                                    placeholder={t('programmes.meant_for_placeholder')}
                                    onChange={(e) => patch({ meantFor: e.target.value.replace(/[<>]/g, '') }, 'meantFor')}
                                    className={`${input} ${meantErr ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                                />
                                {meantErr && <p className="mt-1 text-xs text-red-600">{meantErr}</p>}
                            </div>
                        )}
                        <div className="sm:col-span-2">
                            <label htmlFor="programme-start-label" className="mb-1 block text-xs font-semibold text-gray-600">{t('programmes.start_label_label')}</label>
                            <input
                                id="programme-start-label"
                                data-testid="programme-start-label"
                                type="text"
                                maxLength={LIMITS.startLabel}
                                value={draft.startLabel}
                                placeholder={t('programmes.start_label_placeholder')}
                                onChange={(e) => patch({ startLabel: e.target.value.replace(/[<>]/g, '') }, 'startLabel')}
                                aria-invalid={Boolean(labelErr)}
                                className={`${input} ${labelErr ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                            />
                            {labelErr
                                ? <p data-testid="programme-start-label-error" className="mt-1 text-xs text-red-600">{labelErr}</p>
                                : <p className="mt-1 text-[11px] text-gray-400">{t('programmes.start_label_hint')}</p>}
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                                <CalendarClock className="h-4 w-4 text-[#2D8A8A]" aria-hidden="true" />
                                {t('programmes.count_in')}
                            </span>
                            <div role="radiogroup" aria-label={t('programmes.count_in')} className="flex gap-1 rounded-xl border border-gray-200 bg-white p-0.5" data-testid="programme-unit">
                                {['days', 'weeks'].map((u) => (
                                    <button
                                        key={u}
                                        type="button"
                                        role="radio"
                                        aria-checked={draft.unit === u}
                                        data-testid={`programme-unit-${u}`}
                                        onClick={() => patch(switchDraftUnit(draft, u))}
                                        className={`rounded-lg px-3 py-1 text-xs font-semibold transition-colors ${draft.unit === u
                                            ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white shadow'
                                            : 'text-gray-600 hover:text-[#2D8A8A]'}`}
                                    >
                                        {t(`programmes.unit_${u}`)}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-xs font-semibold text-gray-600">{t('programmes.duration_label')}</span>
                            {!draft.openEnded && (
                                <SpanInput
                                    span={draft.duration}
                                    unit={draft.unit}
                                    onChange={(duration) => patch({ duration })}
                                    invalid={Boolean(durationErr)}
                                    testId="programme-duration"
                                    label={t('programmes.duration_label')}
                                />
                            )}
                            <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-gray-600">
                                <input
                                    type="checkbox"
                                    data-testid="programme-open-ended"
                                    checked={draft.openEnded}
                                    onChange={(e) => patch({ openEnded: e.target.checked })}
                                    className="h-4 w-4 accent-[#4ABDBD]"
                                />
                                {t('programmes.open_ended')}
                            </label>
                        </div>
                        {durationErr
                            ? <p data-testid="programme-duration-error" className="text-xs text-red-600">{durationErr}</p>
                            : <p className="text-[11px] text-gray-400">{t('programmes.duration_hint')}</p>}
                    </div>

                    <section>
                        <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
                            <div>
                                <h3 className="text-sm font-bold text-gray-800">{t('programmes.milestones_heading')}</h3>
                                <p className="max-w-2xl text-xs text-gray-500">{t('programmes.milestones_hint')}</p>
                            </div>
                            <span className="text-xs text-gray-400">{draft.milestones.length}/{LIMITS.milestonesMax}</span>
                        </div>
                        {!isNew && (
                            <p className="mb-2 flex items-center gap-1.5 text-[11px] text-gray-500">
                                <Info className="h-3.5 w-3.5 text-[#2D8A8A]" aria-hidden="true" />
                                {t('programmes.version_note')}
                            </p>
                        )}
                        {showErrors && errors.milestones && (
                            <p data-testid="programme-milestones-error" className="mb-2 text-xs text-red-600">{tr(errors.milestones)}</p>
                        )}

                        <ol className="flex flex-col gap-2" data-testid="programme-milestones">
                            {draft.milestones.map((m, index) => {
                                const nameE = milestoneErr(index, 'name');
                                const offsetE = milestoneErr(index, 'offset');
                                const windowE = milestoneErr(index, 'window');
                                const srvErr = serverErrors[index];
                                const flagged = nameE || offsetE || windowE || srvErr;
                                return (
                                    <li key={m.uid} data-testid={`programme-milestone-${index}`} className={`rounded-xl border bg-white p-3 shadow-sm ${flagged ? 'border-red-200' : 'border-gray-100'}`}>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#4ABDBD]/10 text-xs font-bold text-[#2D8A8A]" aria-hidden="true">
                                                {index + 1}
                                            </span>
                                            <input
                                                type="text"
                                                data-testid={`programme-milestone-${index}-name`}
                                                aria-label={t('programmes.milestone_name')}
                                                maxLength={LIMITS.milestoneName}
                                                value={m.name}
                                                placeholder={t('programmes.milestone_name_placeholder')}
                                                onChange={(e) => patchMilestone(index, { name: e.target.value.replace(/[<>]/g, '') })}
                                                className={`min-w-[160px] flex-1 ${input} ${nameE ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                                            />
                                            <div className="ml-auto flex items-center gap-0.5">
                                                <button type="button" data-testid={`programme-milestone-${index}-up`} onClick={() => moveMilestone(index, -1)} disabled={index === 0} aria-label={t('programmes.move_up')} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30">
                                                    <ArrowUp className="h-4 w-4" aria-hidden="true" />
                                                </button>
                                                <button type="button" data-testid={`programme-milestone-${index}-down`} onClick={() => moveMilestone(index, 1)} disabled={index === draft.milestones.length - 1} aria-label={t('programmes.move_down')} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30">
                                                    <ArrowDown className="h-4 w-4" aria-hidden="true" />
                                                </button>
                                                <button type="button" data-testid={`programme-milestone-${index}-remove`} onClick={() => removeMilestone(index)} aria-label={t('programmes.remove_milestone')} className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
                                                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[auto_auto] sm:justify-start md:items-end">
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t('programmes.milestone_offset')}</span>
                                                <SpanInput
                                                    span={m.offset}
                                                    unit={draft.unit}
                                                    onChange={(offset) => patchMilestone(index, { offset })}
                                                    invalid={Boolean(offsetE)}
                                                    testId={`programme-milestone-${index}-offset`}
                                                    label={t('programmes.milestone_offset')}
                                                />
                                            </div>
                                            <label className="flex flex-col gap-1">
                                                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t('programmes.milestone_window')}</span>
                                                <span className="inline-flex items-center gap-1.5">
                                                    <span className="text-sm text-gray-500">±</span>
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        data-testid={`programme-milestone-${index}-window`}
                                                        value={m.windowDays}
                                                        onChange={(e) => patchMilestone(index, { windowDays: digitsOnly(e.target.value) })}
                                                        className={`w-16 rounded-xl border px-2 py-2 text-center text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40 focus:border-[#4ABDBD] ${windowE ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                                                    />
                                                    <span className="text-xs text-gray-500">{t('programmes.days_short')}</span>
                                                </span>
                                            </label>
                                        </div>

                                        {[nameE, offsetE, windowE, srvErr].filter(Boolean).map((msg) => (
                                            <p key={msg} data-testid={`programme-milestone-${index}-error`} className="mt-1 text-xs text-red-600">{msg}</p>
                                        ))}
                                    </li>
                                );
                            })}
                        </ol>

                        <button
                            type="button"
                            data-testid="programme-add-milestone"
                            onClick={addMilestone}
                            disabled={draft.milestones.length >= LIMITS.milestonesMax}
                            className="mt-2 inline-flex items-center gap-1.5 rounded-xl border border-dashed border-[#4ABDBD] px-3 py-2 text-sm font-semibold text-[#2D8A8A] hover:bg-[#4ABDBD]/10 disabled:opacity-50"
                        >
                            <Plus className="h-4 w-4" aria-hidden="true" />
                            {t('programmes.add_milestone')}
                        </button>
                    </section>

                    {serverError && (
                        <p role="alert" data-testid="programme-server-error" className="flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-xs font-medium text-red-700">
                            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                            {serverError}
                        </p>
                    )}
                    {showErrors && hasProgrammeErrors(errors) && (
                        <p data-testid="programme-fix-above" className="text-xs font-medium text-red-600">{t('programmes.err_fix_above')}</p>
                    )}
                </div>

            </ModalShell>
        </div>,
        document.body
    );
}

export default ProgrammeEditor;
